import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import {
  pushMessagesApi,
  type GetPushMessagesParams,
  type PushMessage,
  type PushSendStatus,
  type PushTargetApp,
} from '@/services/api';
import {
  PageHeader,
  FilterBar,
  FilterField,
  TextInput,
  Select,
  Pagination,
} from '@/components/common';
import {
  DataGrid,
  DateCell,
  makeActionCell,
  makeBadgeCell,
  NumberCell,
  RowNumberCell,
  TruncatedTextCell,
} from '@/components/grid';

type TargetFilter = PushTargetApp | 'ALL';
type StatusFilter = PushSendStatus | 'ALL';

const PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

const TARGET_TONE: Record<PushTargetApp, 'info' | 'success'> = {
  BUSINESS: 'info',
  GUARDIAN: 'success',
};

const TARGET_LABEL: Record<PushTargetApp, string> = {
  BUSINESS: '비즈니스앱',
  GUARDIAN: '보호자앱',
};

const STATUS_TONE: Record<PushSendStatus, 'success' | 'info' | 'danger'> = {
  SENT: 'success',
  SCHEDULED: 'info',
  CANCELED: 'danger',
};

const STATUS_LABEL: Record<PushSendStatus, string> = {
  SENT: '발송 완료',
  SCHEDULED: '예약됨',
  CANCELED: '예약 취소',
};

export function PushMessageListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pageIndex, setPageIndex] = useState(0);
  const [size, setSize] = useState(10);
  const [appliedSearch, setAppliedSearch] = useState<string | undefined>();
  const [appliedTarget, setAppliedTarget] = useState<TargetFilter>('ALL');
  const [appliedStatus, setAppliedStatus] = useState<StatusFilter>('ALL');

  const [localSearch, setLocalSearch] = useState('');
  const [localTarget, setLocalTarget] = useState<TargetFilter>('ALL');
  const [localStatus, setLocalStatus] = useState<StatusFilter>('ALL');
  const [localSize, setLocalSize] = useState(10);

  const queryParams = useMemo<GetPushMessagesParams>(
    () => ({
      page: pageIndex,
      size,
      targetApp: appliedTarget,
      status: appliedStatus,
      search: appliedSearch,
    }),
    [pageIndex, size, appliedTarget, appliedStatus, appliedSearch],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['push-messages', queryParams],
    queryFn: () => pushMessagesApi.getPushMessages(queryParams),
    placeholderData: prev => prev,
    staleTime: 30 * 1000,
  });

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const startIndex = pageIndex * size;

  const cancelMutation = useMutation({
    mutationFn: (id: number) => pushMessagesApi.cancelPushMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-messages'] });
    },
    onError: err => {
      alert(err instanceof Error ? err.message : '예약 취소에 실패했습니다.');
    },
  });

  const handleSearch = () => {
    setPageIndex(0);
    setSize(localSize);
    setAppliedSearch(localSearch.trim() || undefined);
    setAppliedTarget(localTarget);
    setAppliedStatus(localStatus);
  };

  const handleReset = () => {
    setLocalSearch('');
    setLocalTarget('ALL');
    setLocalStatus('ALL');
    setLocalSize(10);
    setPageIndex(0);
    setSize(10);
    setAppliedSearch(undefined);
    setAppliedTarget('ALL');
    setAppliedStatus('ALL');
  };

  const columns = useMemo<ColDef<PushMessage>[]>(
    () => [
      {
        headerName: '번호',
        width: 72,
        sortable: false,
        cellRenderer: RowNumberCell,
        cellRendererParams: { startIndex },
      },
      {
        headerName: '대상',
        field: 'targetApp',
        width: 110,
        cellRenderer: makeBadgeCell({
          toneMap: TARGET_TONE,
          labelMap: TARGET_LABEL,
        }),
      },
      {
        headerName: '상태',
        field: 'sendStatus',
        width: 110,
        cellRenderer: makeBadgeCell({
          toneMap: STATUS_TONE,
          labelMap: STATUS_LABEL,
        }),
      },
      {
        headerName: '제목',
        field: 'title',
        flex: 1.4,
        minWidth: 200,
        cellRenderer: TruncatedTextCell,
      },
      {
        headerName: '발송/예약 시각',
        width: 170,
        sortable: false,
        valueGetter: p => {
          const row = p.data;
          if (!row) return '';
          if (row.sendStatus === 'SCHEDULED' && row.scheduledAt)
            return row.scheduledAt.slice(0, 16);
          if (row.sentAt) return row.sentAt.slice(0, 16);
          return '-';
        },
      },
      {
        headerName: '발송 수',
        field: 'sentCount',
        width: 100,
        cellRenderer: NumberCell,
        type: 'numericColumn',
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        },
      },
      {
        headerName: '등록일',
        field: 'createdAt',
        width: 160,
        cellRenderer: DateCell,
      },
      {
        headerName: '관리',
        width: 160,
        sortable: false,
        cellRenderer: makeActionCell<PushMessage>([
          {
            label: '취소',
            variant: 'danger',
            show: row => row.sendStatus === 'SCHEDULED',
            onClick: row => {
              if (window.confirm('예약된 Push 발송을 취소하시겠습니까?')) {
                cancelMutation.mutate(row.id);
              }
            },
          },
        ]),
      },
    ],
    [startIndex, cancelMutation],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Push 발송"
        subtitle="운영자가 직접 Push 메시지를 즉시 또는 예약 발송합니다."
        right={
          <button
            type="button"
            onClick={() => navigate('/push-messages/new')}
            className="whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            새 Push 발송
          </button>
        }
      />

      <FilterBar onSubmit={handleSearch} onReset={handleReset}>
        <FilterField>
          <TextInput
            placeholder="제목/본문 검색"
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
          />
        </FilterField>
        <FilterField flex="flex-none" className="md:w-40">
          <Select
            value={localTarget}
            onChange={e => setLocalTarget(e.target.value as TargetFilter)}
          >
            <option value="ALL">전체 대상</option>
            <option value="BUSINESS">비즈니스앱</option>
            <option value="GUARDIAN">보호자앱</option>
          </Select>
        </FilterField>
        <FilterField flex="flex-none" className="md:w-40">
          <Select
            value={localStatus}
            onChange={e => setLocalStatus(e.target.value as StatusFilter)}
          >
            <option value="ALL">전체 상태</option>
            <option value="SCHEDULED">예약됨</option>
            <option value="SENT">발송 완료</option>
            <option value="CANCELED">예약 취소</option>
          </Select>
        </FilterField>
        <FilterField flex="flex-none" className="md:w-28">
          <Select
            value={localSize}
            onChange={e => setLocalSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      <DataGrid<PushMessage>
        columns={columns}
        data={rows}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        error={error}
        onRetry={() => refetch()}
        getRowId={row => String(row.id)}
        emptyMessage="발송 내역이 없습니다."
      />

      <Pagination
        pageIndex={pageIndex}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={size}
        onPageChange={setPageIndex}
      />
    </div>
  );
}
